import { normalizeForMatch } from './routing.ts';

/**
 * Possible-duplicate detection for the contacts directory. It only ever suggests: a person reviews every pair and
 * either merges (reversibly) or marks it "not the same". Pairs are compared only within blocks that share a key
 * (name, reordered name, email name, phone ending, name skeleton), so cost grows with block sizes, not N².
 */
export interface PersonLite {
  id: string;
  name: string;
  nameArabic: string | null;
  emails: string[];
  phones: string[];
  accountIds: string[];
}

export type Reason =
  | 'same_name'
  | 'same_name_reordered'
  | 'similar_name'
  | 'same_name_across_scripts'
  | 'same_email_name'
  | 'same_phone_ending'
  | 'same_company'
  | 'common_name';

export interface DuplicatePair {
  a: string;
  b: string;
  score: number;
  reasons: Reason[];
}

const HONORIFICS = new Set([
  'mr',
  'mrs',
  'ms',
  'miss',
  'dr',
  'eng',
  'engr',
  'sheikh',
  'shaikh',
  'prof',
  'sir',
  'madam',
  'السيد',
  'السيده',
  'الشيخ',
  'الشيخه',
  'د',
  'م',
  'الدكتور',
  'الدكتوره',
  'المهندس',
  'الاستاذ',
]);
const GENERIC_EMAIL =
  /^(info|admin|sales|contact|support|hello|office|reservations?|bookings?|noreply|no-reply|enquiries|inquiries)$/;

export function nameTokens(name: string): string[] {
  return normalizeForMatch(name)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((t) => t && !HONORIFICS.has(t));
}

const AR: Record<string, string> = {
  ب: 'b',
  ت: 't',
  ث: 't',
  ج: 'j',
  ح: 'h',
  خ: 'k',
  د: 'd',
  ذ: 'd',
  ر: 'r',
  ز: 'z',
  س: 's',
  ش: 's',
  ص: 's',
  ض: 'd',
  ط: 't',
  ظ: 'd',
  غ: 'g',
  ف: 'f',
  ق: 'k',
  ك: 'k',
  ل: 'l',
  م: 'm',
  ن: 'n',
  ه: 'h',
  پ: 'b',
  چ: 'j',
  گ: 'g',
};

/**
 * A script-independent consonant skeleton of a name, so "فاطمة المنصوري" and "Fatima Al Mansoori" compare equal:
 * vowels, the article (al-, el-, ال) and doubled letters are dropped and similar sounds share one letter.
 */
export function nameSkeleton(name: string): string {
  return nameTokens(name)
    .map((tok) => {
      let t = tok;
      if (/^[؀-ۿ]/.test(t)) {
        // A final ه (including taa marbuta, already folded into ه) matches the dropped Latin final h.
        t = t
          .replace(/^ال/, '')
          .replace(/^(عبد)ال/, '$1')
          .replace(/ه$/, '');
        t = [...t].map((ch) => AR[ch] ?? '').join('');
      } else {
        if (t === 'al' || t === 'el' || t === 'bin' || t === 'bint' || t === 'ibn') return '';
        t = t
          .replace(/^(al|el)-?(?=[a-z]{3})/, '')
          .replace(/kh/g, 'k')
          .replace(/sh/g, 's')
          .replace(/th/g, 't')
          .replace(/dh/g, 'd')
          .replace(/gh/g, 'g')
          .replace(/ch/g, 's')
          .replace(/q/g, 'k')
          .replace(/c/g, 'k')
          .replace(/x/g, 'ks')
          .replace(/h$/, '')
          .replace(/[aeiouyw']/g, '');
      }
      return t.replace(/(.)\1+/g, '$1');
    })
    .filter(Boolean)
    .join(' ');
}

/** Edit distance (insertions, deletions, substitutions, adjacent swaps), capped for speed. */
export function editDistance(a: string, b: string, cap = 3): number {
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i]![j] = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1])
        d[i]![j] = Math.min(d[i]![j]!, d[i - 2]![j - 2]! + 1);
    }
  return d[a.length]![b.length]!;
}

/**
 * A typo, not a different name: the same words, except one word that differs by one letter (two for words longer
 * than seven letters). "Mansouri"/"Mansoori" and "Safron"/"Saffron" qualify; "Mansoori"/"Hashimi" and
 * "Motors"/"Electronics" do not, however long the shared start of the name.
 */
export function isTypo(a: string, b: string): boolean {
  const ta = a.split(' ').filter(Boolean);
  const tb = b.split(' ').filter(Boolean);
  if (a === b || ta.length !== tb.length) return false;
  let differing = 0;
  for (let i = 0; i < ta.length; i++) {
    if (ta[i] === tb[i]) continue;
    if (++differing > 1) return false;
    const longest = Math.max(ta[i]!.length, tb[i]!.length);
    if (longest < 4) return false;
    if (editDistance(ta[i]!, tb[i]!) > (longest > 7 ? 2 : 1)) return false;
  }
  return differing === 1;
}

/** Jaro-Winkler similarity (0..1). */
export function jaroWinkler(s1: string, s2: string): number {
  if (s1 === s2) return 1;
  const range = Math.max(0, Math.floor(Math.max(s1.length, s2.length) / 2) - 1);
  const m1 = new Array<boolean>(s1.length).fill(false);
  const m2 = new Array<boolean>(s2.length).fill(false);
  let matches = 0;
  for (let i = 0; i < s1.length; i++) {
    for (let j = Math.max(0, i - range); j < Math.min(s2.length, i + range + 1); j++) {
      if (m2[j] || s1[i] !== s2[j]) continue;
      m1[i] = m2[j] = true;
      matches++;
      break;
    }
  }
  if (!matches) return 0;
  let t = 0;
  let k = 0;
  for (let i = 0; i < s1.length; i++) {
    if (!m1[i]) continue;
    while (!m2[k]) k++;
    if (s1[i] !== s2[k]) t++;
    k++;
  }
  const jaro = (matches / s1.length + matches / s2.length + (matches - t / 2) / matches) / 3;
  let prefix = 0;
  while (prefix < 4 && s1[prefix] && s1[prefix] === s2[prefix]) prefix++;
  return jaro + prefix * 0.1 * (1 - jaro);
}

const WEIGHT: Record<Exclude<Reason, 'common_name'>, number> = {
  same_name: 0.55,
  same_name_reordered: 0.5,
  same_name_across_scripts: 0.5,
  similar_name: 0.35,
  same_email_name: 0.45,
  same_phone_ending: 0.5,
  same_company: 0.2,
};
export const SUGGEST_THRESHOLD = 0.5;

const emailName = (e: string) => e.split('@')[0]!.replace(/[._+-]/g, '');
const phoneEnding = (p: string) => p.replace(/\D/g, '').slice(-9);

/** Scores one pair. `nameCount` is how many people in the tenant share a's normalised name. */
export function scorePair(a: PersonLite, b: PersonLite, nameCount = 1): DuplicatePair {
  const reasons: Reason[] = [];
  const na = nameTokens(a.name).join(' ');
  const nb = nameTokens(b.name).join(' ');
  if (na && na === nb) reasons.push('same_name');
  else if (na && [...na.split(' ')].sort().join(' ') === [...nb.split(' ')].sort().join(' '))
    reasons.push('same_name_reordered');
  else if (na.length >= 6 && nb.length >= 6 && isTypo(na, nb)) reasons.push('similar_name');
  if (!reasons.length) {
    const sa = [a.name, a.nameArabic].filter(Boolean).map((x) => nameSkeleton(x!));
    const sb = [b.name, b.nameArabic].filter(Boolean).map((x) => nameSkeleton(x!));
    if (sa.some((x) => x.length >= 4 && sb.includes(x))) reasons.push('same_name_across_scripts');
  }
  const ea = new Set(a.emails.map(emailName).filter((x) => x.length >= 4 && !GENERIC_EMAIL.test(x)));
  if (b.emails.some((e) => ea.has(emailName(e)) && !a.emails.includes(e))) reasons.push('same_email_name');
  const pa = new Set(a.phones.map(phoneEnding).filter((x) => x.length >= 8));
  if (b.phones.some((p) => pa.has(phoneEnding(p)) && !a.phones.includes(p)))
    reasons.push('same_phone_ending');
  const nameSignal = reasons.some((r) => r.includes('name') && r !== 'same_email_name');
  if (nameSignal && a.accountIds.some((x) => b.accountIds.includes(x))) reasons.push('same_company');

  let score = 1 - reasons.reduce((p, r) => p * (1 - (WEIGHT[r as keyof typeof WEIGHT] ?? 0)), 1);
  // A common name on its own is weak evidence ("two Mohammed Alis"): keep it below the suggestion threshold.
  const onlyNames = reasons.every((r) => r.startsWith('same_name') || r === 'similar_name');
  if (nameCount >= 3 && onlyNames) {
    reasons.push('common_name');
    score = Math.min(score, SUGGEST_THRESHOLD - 0.05);
  }
  const [x, y] = a.id < b.id ? [a.id, b.id] : [b.id, a.id];
  return { a: x, b: y, score: Math.round(score * 100) / 100, reasons };
}

/** Candidate pairs above the threshold, best first. Dismissed pairs ("a|b", smaller id first) are skipped. */
export function findDuplicates(
  people: PersonLite[],
  dismissed: ReadonlySet<string> = new Set(),
): DuplicatePair[] {
  const blocks = new Map<string, PersonLite[]>();
  const nameCount = new Map<string, number>();
  const add = (key: string, p: PersonLite) => {
    const list = blocks.get(key) ?? [];
    if (list.length < 50) list.push(p);
    blocks.set(key, list);
  };
  for (const p of people) {
    const tokens = nameTokens(p.name);
    const full = tokens.join(' ');
    if (full) {
      nameCount.set(full, (nameCount.get(full) ?? 0) + 1);
      add(`n:${full}`, p);
      add(`s:${[...tokens].sort().join(' ')}`, p);
      if (tokens.length >= 2) add(`f:${tokens[0]}:${tokens.at(-1)!.slice(0, 3)}`, p);
    }
    for (const n of [p.name, p.nameArabic]) if (n) add(`k:${nameSkeleton(n)}`, p);
    for (const e of p.emails)
      if (emailName(e).length >= 4 && !GENERIC_EMAIL.test(emailName(e))) add(`e:${emailName(e)}`, p);
    for (const ph of p.phones) if (phoneEnding(ph).length >= 8) add(`p:${phoneEnding(ph)}`, p);
  }
  const seen = new Set<string>();
  const out: DuplicatePair[] = [];
  for (const list of blocks.values()) {
    for (let i = 0; i < list.length; i++)
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i]!;
        const b = list[j]!;
        if (a.id === b.id) continue;
        const key = a.id < b.id ? `${a.id}|${b.id}` : `${b.id}|${a.id}`;
        if (seen.has(key) || dismissed.has(key)) continue;
        seen.add(key);
        const pair = scorePair(a, b, nameCount.get(nameTokens(a.name).join(' ')) ?? 1);
        if (pair.score >= SUGGEST_THRESHOLD) out.push(pair);
      }
  }
  return out.sort((x, y) => y.score - x.score);
}
